import * as React from 'react';
import { Alert, Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
    BETUP_FEED_LABELS, getBetupFeedUserViolations, rejectBetupFeedPost, resolveBetupFeedMessageReport,
    resolveBetupFeedPostReports,
} from '../../Api';
import { BetupFeedReviewCategory, BetupFeedUserViolationDto } from '../../models/dtos/BetupFeedPostDto';
import {
    ConfirmDialog, FeedPlayer, formatFeedDate, loadErrorText, PostText, RejectPostDialog, ReviewStatusChip, shorten,
} from './BetupFeedShared';

const MESSAGE_SOURCE_LABELS: Record<string, string> = {
    FEED_COMMENT: 'Feed comment',
    MATCH_CHAT: 'Match chat',
    PRIVATE_MESSAGE: 'Private message',
};

type PendingAction = { kind: 'resolve' | 'reject'; row: BetupFeedUserViolationDto } | null;

const isPostReport = (row: BetupFeedUserViolationDto) => row.kind === 'POST_REPORT';

function subjectText(row: BetupFeedUserViolationDto): string {
    return isPostReport(row)
        ? `Post #${row.post_id}: “${shorten(row.text)}”`
        : `${MESSAGE_SOURCE_LABELS[row.source ?? ''] ?? row.source ?? 'Message'}: “${shorten(row.text)}”`;
}

/** User page tab: reports other players made about the user's posts and messages, newest first. */
export default function UserViolationsTab({ userId }: { userId: number }) {
    const [rows, setRows] = React.useState<BetupFeedUserViolationDto[]>([]);
    const [rowCount, setRowCount] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({ page: 0, pageSize: 20 });
    const [error, setError] = React.useState<string | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [pending, setPending] = React.useState<PendingAction>(null);
    const [busy, setBusy] = React.useState(false);

    const load = React.useCallback(async () => {
        setLoading(true);
        const page = await getBetupFeedUserViolations(userId, paginationModel.page * paginationModel.pageSize, paginationModel.pageSize);
        setLoading(false);
        if (page == null) {
            setError(loadErrorText(BETUP_FEED_LABELS.userViolations, 'Failed to load violation reports.'));
            setRows([]);
            setRowCount(0);
            return;
        }
        setRows(page.items ?? []);
        setRowCount(page.totalItems ?? 0);
    }, [userId, paginationModel]);

    React.useEffect(() => { load(); }, [load]);

    const runAction = async (reason?: string, categories?: BetupFeedReviewCategory[]) => {
        if (pending == null) return;
        const { kind, row } = pending;
        setPending(null);
        setBusy(true);
        setNotice(null);
        let result: { error: string | null };
        let done: string;
        if (kind === 'reject' && row.post_id != null) {
            result = await rejectBetupFeedPost(row.post_id, reason ?? '', categories ?? []);
            done = `Post #${row.post_id} rejected and its reports marked reviewed — the author sees your reason.`;
        } else if (isPostReport(row) && row.post_id != null) {
            result = await resolveBetupFeedPostReports(row.post_id);
            done = `Reports on post #${row.post_id} marked reviewed.`;
        } else {
            result = await resolveBetupFeedMessageReport(row.id);
            done = `Message report #${row.id} marked reviewed.`;
        }
        setBusy(false);
        if (result.error != null) {
            setError(`Action failed: ${result.error}`);
            return;
        }
        setError(null);
        setNotice(done);
        await load();
    };

    const columns: GridColDef<BetupFeedUserViolationDto>[] = [
        {
            field: 'kind', headerName: 'Kind', width: 150, sortable: false,
            renderCell: ({ row }) => isPostReport(row)
                ? <Chip size="small" color="primary" variant="outlined" label="Post report" />
                : (
                    <Stack gap={0.5} alignItems="flex-start">
                        <Chip size="small" color="secondary" variant="outlined" label="Message report" />
                        {row.source && (
                            <Chip size="small" variant="outlined" color={row.source === 'MATCH_CHAT' ? 'info' : 'default'}
                                label={MESSAGE_SOURCE_LABELS[row.source] ?? row.source} />
                        )}
                    </Stack>
                ),
        },
        {
            field: 'created_at', headerName: 'Reported', width: 160, sortable: false,
            renderCell: ({ row }) => (
                <Stack>
                    <Typography variant="body2">{formatFeedDate(row.created_at)}</Typography>
                    <Typography variant="caption" color="text.secondary">report #{row.id}</Typography>
                </Stack>
            ),
        },
        {
            field: 'reporter', headerName: 'Reporter', width: 180, sortable: false,
            renderCell: ({ row }) => <FeedPlayer player={row.reporter} dense />,
        },
        {
            field: 'reason', headerName: 'Reason', width: 180, sortable: false,
            renderCell: ({ row }) => row.reason
                ? <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{row.reason}</Typography>
                : <Typography variant="body2" color="text.secondary">—</Typography>,
        },
        {
            field: 'text', headerName: 'Reported text', flex: 1, minWidth: 240, sortable: false,
            renderCell: ({ row }) => (
                <Stack gap={0.75} sx={{ width: '100%' }}>
                    {isPostReport(row) && (
                        <Stack direction="row" gap={0.75} alignItems="center">
                            <Typography variant="caption" color="text.secondary">post #{row.post_id ?? '?'}</Typography>
                            {row.post_review_status && <ReviewStatusChip status={row.post_review_status} />}
                        </Stack>
                    )}
                    <PostText text={row.text} />
                </Stack>
            ),
        },
        {
            field: 'status', headerName: 'Status', width: 170, sortable: false,
            renderCell: ({ row }) => row.resolved_at
                ? <Typography variant="body2" color="success.main">reviewed {formatFeedDate(row.resolved_at)}</Typography>
                : <Chip size="small" color="error" label="open" />,
        },
        {
            field: 'actions', headerName: 'Actions', width: 150, sortable: false,
            renderCell: ({ row }) => (
                <Stack gap={1}>
                    {row.resolved_at == null && (
                        <Button size="small" variant="outlined" color="success" disabled={busy}
                            onClick={() => setPending({ kind: 'resolve', row })}>Mark reviewed</Button>
                    )}
                    {isPostReport(row) && row.post_id != null && row.post_review_status !== 'REJECTED' && (
                        <Button size="small" variant="outlined" color="error" disabled={busy}
                            onClick={() => setPending({ kind: 'reject', row })}>Reject post</Button>
                    )}
                </Stack>
            ),
        },
    ];

    const subject = pending == null ? '' : (
        <Tooltip title={pending.row.text ?? ''}><span>{subjectText(pending.row)}</span></Tooltip>
    );

    return (
        <Stack gap={2} sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
                Reports other players made about this user's posts and messages, newest first. Mark reviewed dismisses a
                report and keeps the content; Reject post takes the post off the feed with your reason and categories.
            </Typography>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                getRowId={row => `${row.kind}-${row.id}`}
                getRowHeight={() => 'auto'}
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 20, 50]}
                loading={loading}
                disableRowSelectionOnClick
                disableColumnMenu
                sx={{ '& .MuiDataGrid-cell': { py: 1.5, alignItems: 'flex-start' } }}
                localeText={{ noRowsLabel: 'No reports about this user' }}
            />
            <ConfirmDialog
                open={pending?.kind === 'resolve'}
                title={pending != null && isPostReport(pending.row) ? 'Mark reports reviewed?' : 'Mark report reviewed?'}
                confirmLabel="Mark reviewed"
                confirmColor="success"
                onClose={() => setPending(null)}
                onConfirm={() => runAction()}
                text={pending == null ? '' : (
                    <>
                        {subject}
                        <br />
                        {isPostReport(pending.row)
                            ? 'All open reports on this post are marked reviewed. The post itself is not changed.'
                            : 'The report leaves the open list. The message itself is not changed.'}
                    </>
                )}
            />
            <RejectPostDialog
                open={pending?.kind === 'reject'}
                subject={subject}
                onClose={() => setPending(null)}
                onConfirm={(reason, categories) => runAction(reason, categories)}
            />
        </Stack>
    );
}
